import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './partnerAddPopup.scss';

const DISPLAY_DURATION_MS = 4000;
const EXIT_DURATION_MS = 400;
const TYPEWRITER_DELAY_MS = 1000;
const TYPEWRITER_CHAR_DELAY_MS = 100;
const BLANK_IMAGE = '/img/food-grey.jpg';

const PartnerAddPopup = ({ visible, title, dishName, imageUrl, typewriterText, onCloseStart, onClose }) => {
  const [mounted, setMounted] = useState(false);
  const [typed, setTyped] = useState('');
  const [closing, setClosing] = useState(false);
  const [resolvedImageUrl, setResolvedImageUrl] = useState(BLANK_IMAGE);
  const [imageLoaded, setImageLoaded] = useState(false);
  const timersRef = useRef([]);
  const onCloseRef = useRef(onClose);
  const onCloseStartRef = useRef(onCloseStart);
  const imageRetryRef = useRef(0);
  const imageWrapRef = useRef(null);
  const resolvedImageUrlRef = useRef(BLANK_IMAGE);

  onCloseRef.current = onClose;
  onCloseStartRef.current = onCloseStart;
  resolvedImageUrlRef.current = resolvedImageUrl;

  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    if (!visible) {
      setMounted(false);
      setClosing(false);
      setTyped('');
      setResolvedImageUrl(BLANK_IMAGE);
      setImageLoaded(false);
      imageRetryRef.current = 0;
      return undefined;
    }

    const fullText = typewriterText || '';

    setMounted(true);
    setClosing(false);
    setTyped('');
    setResolvedImageUrl(imageUrl || BLANK_IMAGE);
    setImageLoaded(false);
    imageRetryRef.current = 0;

    for (let i = 0; i < fullText.length; i += 1) {
      const timer = setTimeout(() => {
        setTyped(fullText.slice(0, i + 1));
      }, TYPEWRITER_DELAY_MS + i * TYPEWRITER_CHAR_DELAY_MS);
      timersRef.current.push(timer);
    }

    const closeTimer = setTimeout(() => {
      if (onCloseStartRef.current) {
        onCloseStartRef.current({
          rect: imageWrapRef.current ? imageWrapRef.current.getBoundingClientRect() : null,
          imageUrl: resolvedImageUrlRef.current || imageUrl || BLANK_IMAGE,
          dishName,
        });
      }
      setClosing(true);
    }, DISPLAY_DURATION_MS);
    timersRef.current.push(closeTimer);

    const unmountTimer = setTimeout(() => {
      setMounted(false);
      if (onCloseRef.current) {
        onCloseRef.current();
      }
    }, DISPLAY_DURATION_MS + EXIT_DURATION_MS);
    timersRef.current.push(unmountTimer);

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [visible, typewriterText, dishName, imageUrl]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setResolvedImageUrl(imageUrl || BLANK_IMAGE);
    setImageLoaded(false);
    imageRetryRef.current = 0;
  }, [imageUrl, visible]);

  const handleImageError = () => {
    if (!imageUrl) {
      setResolvedImageUrl(BLANK_IMAGE);
      return;
    }

    if (imageRetryRef.current === 0) {
      imageRetryRef.current = 1;
      setImageLoaded(false);
      setResolvedImageUrl(`${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`);
      return;
    }

    setImageLoaded(true);
    setResolvedImageUrl(BLANK_IMAGE);
  };

  if (!mounted) {
    return null;
  }

  return createPortal(
    <div className={`partner-add-popup-overlay ${closing ? 'is-closing' : ''}`}>
      <div className="partner-add-popup-title">{title}</div>
      <div className="partner-add-popup-card">
        <div ref={imageWrapRef} className={`partner-add-popup-image-wrap ${imageLoaded ? 'is-loaded' : ''}`}>
          <img
            key={resolvedImageUrl}
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            src={resolvedImageUrl}
            alt={dishName || ''}
            onLoad={() => setImageLoaded(true)}
            onError={handleImageError}
          />
        </div>
        <div className="partner-add-popup-dish-name">{dishName}</div>
      </div>
      <div className="partner-add-popup-typewriter">
        <span className="partner-add-popup-typewriter-text">{typed}</span>
        <span className="partner-add-popup-caret">|</span>
      </div>
    </div>,
    document.body
  );
};

export default PartnerAddPopup;
